import React, { useState, useEffect, useCallback } from 'react';
import DemographicSubmissionForm from '../components/DemographicForm';
import DemographicSafetyBar from '../components/DemographicSatefyBar';
import DemographicPendingBanner from '../components/DemographicPendingBanner';
import { fetchDemographicStatus } from '../lib/demographicStatus';


export default function DemographicGate({ currentUser, children, safetyActions = [] }) {
  const [state, setState] = useState({ loading: true, status: null, detail: null });
  const [bannerDismissed, setBannerDismissed] = useState(false);

  const loadStatus = useCallback(async () => {
    setState((prev) => ({ ...prev, loading: true }));
    const result = await fetchDemographicStatus(currentUser);
    setState({ loading: false, status: result.status, detail: result });
  }, [currentUser]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  if (!currentUser) return null;

  if (state.loading) {
    // Deliberately minimal — this should be near-instant once the real
    // endpoint is wired. No safety bar needed here since it's transient.
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950">
        <div className="animate-spin h-6 w-6 border-b-2 border-primary-500 rounded-full" />
      </div>
    );
  }

  const { status, detail } = state;

  // verified, or gate is a no-op for this role — pass straight through.
  if (status === 'verified') {
    return children;
  }

  // Non-blocking: dashboard remains fully usable underneath the banner.
  if (status === 'pending_verification') {
    return (
      <>
        <DemographicSafetyBar actions={safetyActions} />
        {!bannerDismissed && (
          <DemographicPendingBanner onDismiss={() => setBannerDismissed(true)} />
        )}
        {children}
      </>
    );
  }

  // no_submission or rejected — hard block, but safety actions stay reachable.
  const isRejected = status === 'rejected';

  return (
    <>
      <DemographicSafetyBar actions={safetyActions} />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Complete your demographic information"
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm pt-12"
      >
        <div className="w-full max-w-lg mx-4 rounded-2xl bg-white shadow-xl p-6 sm:p-8 max-h-[85vh] overflow-y-auto">
          <h2 className="text-xl font-semibold text-gray-900">
            {isRejected ? 'Correction needed' : 'Before you continue'}
          </h2>

          {isRejected ? (
            <div className="mt-2 rounded-lg bg-red-50 border border-red-200 p-3">
              <p className="text-sm text-red-800">
                {detail?.rejection_reason ||
                  'Your submitted demographic information could not be verified.'}
              </p>
              {detail?.correction_notes && (
                <p className="text-sm text-red-700 mt-1">{detail.correction_notes}</p>
              )}
            </div>
          ) : (
            <p className="mt-2 text-sm text-gray-600">
              We need a few details to set up your patient record. This only
              takes a minute, and it's required before you can access your
              dashboard.
            </p>
          )}

          <div className="mt-6">
            <DemographicSubmissionForm
              currentUser={currentUser}
              onSubmitted={loadStatus}
            />
          </div>
        </div>
      </div>
    </>
  );
}